import {
  ArrowRight,
  Check,
  ChevronDown,
  Clock,
  Disc,
  Mic,
  MonitorUp,
  PencilRuler,
  PhoneOff,
  Plug,
  ShieldCheck,
  Sparkles,
  User,
  Users,
  Video,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { AppShell } from "@/components/layout/AppShell";
import { Seo, SHARE_CARD } from "@/components/Seo";
import { Button } from "@/components/ui/Button";
import { useSession } from "@/context/SessionContext";
import { env, siteOrigin } from "@/env";
import { cn } from "@/lib/cn";
import { formatRoomCode, isValidRoomCode, normalizeRoomCode } from "@/lib/room";
import { useT, type Translate } from "@/lib/i18n";

const CODE_INPUT_ID = "join-code";

const FEATURES = [
  { id: "guests", icon: Users, accent: "from-accent-violet to-brand" },
  { id: "scale", icon: Clock, accent: "from-brand to-accent-sky" },
  { id: "screen", icon: MonitorUp, accent: "from-accent-sky to-accent-teal" },
  { id: "board", icon: PencilRuler, accent: "from-accent-amber to-accent-rose" },
  { id: "record", icon: Disc, accent: "from-accent-rose to-accent-amber" },
  { id: "control", icon: ShieldCheck, accent: "from-accent-teal to-accent-sky" },
] as const;

const STEPS = ["1", "2", "3"] as const;

const QUESTIONS = ["account", "limits", "install", "share", "record", "control"] as const;

export function LandingPage() {
  const { signedIn } = useSession();
  const t = useT();

  return (
    <AppShell bleed>
      <Seo
        bare
        description={t("landing.intro", { name: env.appName })}
        imageAlt={`The ${env.appName} home page: start a meeting, or join one with a code`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${siteOrigin()}/#webpage`,
              name: `${env.appName}: video meetings that start in one click`,
              url: siteOrigin(),
              isPartOf: { "@id": `${siteOrigin()}/#website` },
              about: { "@id": `${siteOrigin()}/#app` },
              primaryImageOfPage: `${siteOrigin()}${SHARE_CARD}`,
            },
            {
              "@type": "FAQPage",
              "@id": `${siteOrigin()}/#faq`,
              isPartOf: { "@id": `${siteOrigin()}/#webpage` },
              mainEntity: QUESTIONS.map((id) => ({
                "@type": "Question",
                name: t(`landing.faq.${id}.q`),
                acceptedAnswer: {
                  "@type": "Answer",
                  text: t(`landing.faq.${id}.a`),
                },
              })),
            },
          ],
        }}
        path="/"
        title={`${env.appName}: video meetings that start in one click`}
      />

      <Hero signedIn={signedIn} t={t} />
      <Features t={t} />
      <Steps t={t} />
      <Questions t={t} />
      <Integrate t={t} />
      <Closing signedIn={signedIn} t={t} />
      <MobileBar signedIn={signedIn} t={t} />
    </AppShell>
  );
}

function Section({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section className={cn("mx-auto w-full max-w-6xl px-4 sm:px-6", className)} id={id}>
      {children}
    </section>
  );
}

function Aurora() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute -top-40 -start-24 h-[26rem] w-[26rem] rounded-full bg-brand/25 blur-3xl" />
      <div className="absolute -top-24 end-0 h-[22rem] w-[22rem] rounded-full bg-accent-violet/20 blur-3xl" />
      <div className="absolute top-40 start-1/3 h-[24rem] w-[24rem] rounded-full bg-accent-teal/15 blur-3xl" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-canvas" />
    </div>
  );
}

function Hero({ signedIn, t }: { signedIn: boolean; t: Translate }) {
  const navigate = useNavigate();

  return (
    <div className="relative isolate overflow-hidden pt-8 pb-10 sm:pt-16 sm:pb-20">
      <Aurora />
      <Section className="flex flex-col items-center gap-8 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface/80 px-3 py-1 text-xs font-medium text-ink-muted backdrop-blur">
          <Sparkles aria-hidden className="h-3.5 w-3.5 text-brand" />
          {t("landing.badge")}
        </span>

        <div className="flex max-w-3xl flex-col gap-4">
          {}
          <h1 className="bg-gradient-to-br from-ink via-brand to-accent-teal bg-clip-text py-[0.2em] text-4xl leading-[1.3] font-semibold tracking-tight text-balance text-transparent sm:text-6xl">
            {t("app.tagline")}
          </h1>
          <p className="mx-auto max-w-2xl text-base text-pretty text-ink-muted sm:text-lg">
            {t("landing.intro", { name: env.appName })}
          </p>
        </div>

        <div className="flex w-full max-w-xl flex-col items-stretch gap-3">
          <Button
            className="w-full gap-2 rounded-2xl text-base shadow-panel transition-transform active:scale-[0.99] sm:w-auto sm:self-center"
            onClick={() => void navigate(signedIn ? "/dashboard" : "/register")}
            size="lg"
          >
            {signedIn ? t("landing.goToMeetings") : t("landing.startMeeting")}
            <ArrowRight aria-hidden className="h-4 w-4 rtl:-scale-x-100" />
          </Button>

          <JoinCard inputId={CODE_INPUT_ID} t={t} />
        </div>

        <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-ink-muted">
          {(["install", "browser", "guests", "scale"] as const).map((id) => (
            <li className="inline-flex items-center gap-1.5" key={id}>
              <Check aria-hidden className="h-3.5 w-3.5 text-success" />
              {t(`landing.chip.${id}`)}
            </li>
          ))}
        </ul>

        <Preview t={t} />
      </Section>
    </div>
  );
}

function JoinCard({ inputId, t }: { inputId?: string | undefined; t: Translate }) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const valid = isValidRoomCode(code);

  return (
    <form
      className="flex flex-col gap-2 rounded-2xl border border-line bg-surface/90 p-3 text-start shadow-panel backdrop-blur"
      id="join"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) void navigate(`/${formatRoomCode(code)}`);
      }}
    >
      <label className="px-1 text-xs font-medium text-ink" htmlFor={inputId ?? "join-code-any"}>
        {t("landing.joinTitle")}
      </label>
      <div className="flex items-center gap-2">
        <input
          aria-describedby={`${inputId ?? "join-code-any"}-hint`}
          autoCapitalize="none"
          autoComplete="off"
          className="min-h-12 w-full min-w-0 rounded-xl border border-line bg-canvas px-4 font-mono text-base tracking-[0.2em] text-ink transition-colors placeholder:tracking-[0.2em] placeholder:text-ink-faint focus:border-brand"
          enterKeyHint="go"
          id={inputId ?? "join-code-any"}
          inputMode="text"
          onChange={(event) => {
            setCode(formatRoomCode(normalizeRoomCode(event.target.value).slice(0, 12)));
          }}
          placeholder={t("landing.codePlaceholder")}
          spellCheck={false}
          value={code}
        />
        <Button
          className="min-h-12 shrink-0 rounded-xl px-5 transition-transform active:scale-[0.98]"
          disabled={!valid}
          size="lg"
          type="submit"
        >
          {t("common.join")}
        </Button>
      </div>
      <p className="px-1 text-xs text-ink-faint" id={`${inputId ?? "join-code-any"}-hint`}>
        {t("landing.joinHint")}
      </p>
    </form>
  );
}

function Preview({ t }: { t: Translate }) {
  return (
    <figure className="mt-2 w-full max-w-3xl">
      <div className="rounded-[1.75rem] border border-line bg-stage p-2 shadow-panel sm:p-3">
        <div className="flex items-center gap-2 px-1.5 pb-2">
          <span aria-hidden className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-danger/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-warning/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-success/70" />
          </span>
          <span className="mx-auto rounded-full bg-white/10 px-3 py-1 font-mono text-[11px] text-stage-muted">
            {t("landing.codePlaceholder")}
          </span>
          <span aria-hidden className="h-2.5 w-8 rounded-full bg-white/10" />
        </div>

        {}
        <div className="relative aspect-video w-full overflow-hidden rounded-tile bg-gradient-to-br from-accent-sky/25 via-brand/20 to-accent-violet/25">
          <div className="absolute inset-3 flex flex-col justify-between rounded-xl bg-stage-raised/85 p-4 sm:inset-5 sm:p-6">
            <div>
              {}
              <div className="relative w-2/5">
                <div className="h-2.5 rounded-full bg-white/55 sm:h-3" />
                <svg
                  aria-hidden
                  className="absolute -bottom-2.5 start-0 h-2.5 w-full text-accent-amber"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 100 10"
                >
                  <path
                    d="M2 7C22 3 56 2 98 5"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              </div>
              <div className="mt-5 h-2 w-4/5 rounded-full bg-white/20" />
              <div className="mt-2 h-2 w-3/5 rounded-full bg-white/20" />
            </div>
            {}
            <div className="flex h-1/2 items-end gap-2 sm:gap-3">
              {[34, 58, 44, 72, 50, 88].map((height, index) => (
                <span className="flex h-full items-end" key={height}>
                  {}
                  <span className="relative block w-4 sm:w-7" style={{ height: `${height}%` }}>
                    <span
                      className={cn(
                        "block h-full w-full rounded-t-md",
                        index === 5
                          ? "bg-accent-amber/80"
                          : index % 2 === 0
                            ? "bg-accent-teal/70"
                            : "bg-accent-violet/70",
                      )}
                    />
                    {index === 5 ? (
                      <span
                        aria-hidden
                        className="absolute -inset-x-2 -top-2 -bottom-1.5 -rotate-6 rounded-[50%] border-2 border-accent-amber/80"
                      />
                    ) : null}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {}
        <div className="mt-2 grid grid-cols-4 gap-2">
          {[
            "from-accent-violet/60",
            "from-accent-teal/60",
            "from-accent-amber/60",
            "from-brand/60",
          ].map((accent, index) => (
            <div
              className={cn(
                "relative aspect-video overflow-hidden rounded-xl bg-gradient-to-br to-stage-raised",
                accent,
              )}
              key={accent}
            >
              <User
                aria-hidden
                className="absolute inset-0 m-auto h-6 w-6 text-stage-ink/70 sm:h-7 sm:w-7"
              />
              {index === 1 ? (
                <span className="absolute end-1 bottom-1 rounded-md bg-stage/70 p-1">
                  <Mic aria-hidden className="h-3 w-3 text-danger" />
                </span>
              ) : null}
            </div>
          ))}
        </div>

        <div className="mt-2 flex items-center justify-center gap-2 rounded-2xl bg-stage-raised/70 px-3 py-2">
          {[Mic, Video, MonitorUp, PencilRuler].map((Icon) => (
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-stage-ink"
              key={Icon.displayName ?? Icon.name}
            >
              <Icon aria-hidden className="h-4 w-4" />
            </span>
          ))}
          <span className="flex h-9 w-12 items-center justify-center rounded-full bg-danger text-white">
            <PhoneOff aria-hidden className="h-4 w-4" />
          </span>
        </div>
      </div>
      <figcaption className="sr-only">{t("landing.preview.alt")}</figcaption>
    </figure>
  );
}

function Features({ t }: { t: Translate }) {
  return (
    <Section className="py-12 sm:py-20">
      <Heading body={t("landing.features.body")} title={t("landing.features.title")} />

      {}
      <ul className="scrollbar-none -mx-4 mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0">
        {FEATURES.map(({ id, icon: Icon, accent }) => (
          <li className="w-[78%] shrink-0 snap-center sm:w-auto sm:shrink" key={id}>
            <article className="group h-full rounded-2xl border border-line bg-surface p-5 shadow-panel transition-transform duration-300 ease-out-soft active:scale-[0.99] sm:hover:-translate-y-1">
              <span
                className={cn(
                  "inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-panel",
                  accent,
                )}
              >
                <Icon aria-hidden className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-base font-semibold text-ink">
                {t(`landing.feature.${id}.title`)}
              </h3>
              <p className="mt-1.5 text-sm text-pretty text-ink-muted">
                {t(`landing.feature.${id}.body`)}
              </p>
            </article>
          </li>
        ))}
      </ul>
      <p className="mt-1 text-center text-xs text-ink-faint sm:hidden">{t("landing.swipe")}</p>
    </Section>
  );
}

function Steps({ t }: { t: Translate }) {
  return (
    <div className="bg-surface-sunken py-12 sm:py-20">
      <Section>
        <Heading title={t("landing.how.title")} />
        <ol className="mt-8 grid gap-4 sm:grid-cols-3 sm:gap-6">
          {STEPS.map((number) => (
            <li
              className="relative rounded-2xl border border-line bg-surface p-5 shadow-panel"
              key={number}
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand to-accent-violet text-sm font-semibold text-white">
                {number}
              </span>
              <h3 className="mt-3 text-base font-semibold text-ink">
                {t(`landing.how.${number}.title`)}
              </h3>
              <p className="mt-1.5 text-sm text-pretty text-ink-muted">
                {t(`landing.how.${number}.body`)}
              </p>
            </li>
          ))}
        </ol>
      </Section>
    </div>
  );
}

function Questions({ t }: { t: Translate }) {
  return (
    <Section className="py-12 sm:py-20">
      <Heading title={t("landing.faq.title")} />
      <div className="mx-auto mt-8 flex max-w-3xl flex-col gap-3">
        {QUESTIONS.map((id) => (
          <details
            className="group rounded-2xl border border-line bg-surface px-4 shadow-panel open:bg-surface-raised"
            key={id}
          >
            <summary className="flex min-h-14 list-none items-center justify-between gap-4 py-4 text-start text-sm font-medium text-ink marker:content-none">
              {t(`landing.faq.${id}.q`)}
              <ChevronDown
                aria-hidden
                className="h-4 w-4 shrink-0 text-ink-muted transition-transform duration-200 group-open:rotate-180"
              />
            </summary>
            <p className="pb-4 text-sm text-pretty text-ink-muted">{t(`landing.faq.${id}.a`)}</p>
          </details>
        ))}
      </div>
    </Section>
  );
}

function Integrate({ t }: { t: Translate }) {
  return (
    <Section className="pb-14 sm:pb-20">
      <div className="relative overflow-hidden rounded-3xl border border-line bg-surface p-6 shadow-panel sm:p-10">
        <div
          aria-hidden
          className="pointer-events-none absolute -end-16 -top-16 h-56 w-56 rounded-full bg-accent-sky/20 blur-3xl"
        />
        <div className="relative flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-8">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-sky to-brand text-white">
            <Plug aria-hidden className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-ink">{t("landing.integrate.title")}</h2>
            <p className="mt-1 text-sm text-pretty text-ink-muted">
              {t("landing.integrate.body", { name: env.appName })}
            </p>
          </div>
          <Link
            className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl border border-line bg-canvas px-4 text-sm font-medium text-brand transition-colors hover:bg-surface-raised"
            to="/docs"
          >
            {t("landing.integrate.link")}
            <ArrowRight aria-hidden className="h-4 w-4 rtl:-scale-x-100" />
          </Link>
        </div>
      </div>
    </Section>
  );
}

function Closing({ signedIn, t }: { signedIn: boolean; t: Translate }) {
  const navigate = useNavigate();

  return (
    <Section className="pb-20 sm:pb-24">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-accent-teal via-brand to-brand-strong p-8 text-center shadow-panel sm:p-14">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_0%,rgb(255_255_255/0.25),transparent)]"
        />
        <div className="relative mx-auto flex max-w-xl flex-col items-center gap-5">
          <h2 className="text-2xl font-semibold text-balance text-white sm:text-3xl">
            {t("landing.cta.title")}
          </h2>
          <p className="text-sm text-pretty text-white/85 sm:text-base">{t("landing.cta.body")}</p>
          <Button
            className="min-h-12 w-full gap-2 rounded-2xl bg-white text-base text-brand transition-transform hover:bg-white/90 active:scale-[0.99] sm:w-auto"
            onClick={() => void navigate(signedIn ? "/dashboard" : "/register")}
            size="lg"
          >
            {signedIn ? t("landing.goToMeetings") : t("landing.startMeeting")}
            <ArrowRight aria-hidden className="h-4 w-4 rtl:-scale-x-100" />
          </Button>
        </div>
      </div>
    </Section>
  );
}

function MobileBar({ signedIn, t }: { signedIn: boolean; t: Translate }) {
  const navigate = useNavigate();
  const sentinel = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const element = sentinel.current;
    if (element === null) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShown(entry !== undefined && !entry.isIntersecting);
      },
      { rootMargin: "-120px 0px 0px 0px" },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (globalThis.location.hash !== "#join") return;
    globalThis.document.getElementById(CODE_INPUT_ID)?.focus();
  }, []);

  return (
    <>
      <div aria-hidden className="absolute top-[70vh]" ref={sentinel} />
      <div
        className={cn(
          "pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-line bg-canvas/90 px-4 pt-3 backdrop-blur transition-transform duration-300 ease-out-soft sm:hidden",
          shown ? "translate-y-0" : "translate-y-full",
        )}
      >
        <div className="flex items-center gap-2">
          <Button
            className="min-h-12 flex-1 rounded-2xl transition-transform active:scale-[0.98]"
            onClick={() => void navigate(signedIn ? "/dashboard" : "/register")}
          >
            {signedIn ? t("landing.goToMeetings") : t("landing.startMeeting")}
          </Button>
          <Button
            className="min-h-12 flex-1 rounded-2xl transition-transform active:scale-[0.98]"
            onClick={() => {
              const field = document.getElementById(CODE_INPUT_ID);
              field?.scrollIntoView({ behavior: "smooth", block: "center" });
              field?.focus({ preventScroll: true });
            }}
            variant="secondary"
          >
            {t("landing.joinTitle")}
          </Button>
        </div>
      </div>
    </>
  );
}

function Heading({ title, body }: { title: string; body?: string }) {
  return (
    <div className="mx-auto flex max-w-2xl flex-col items-center gap-2 text-center">
      <h2 className="text-2xl font-semibold tracking-tight text-balance text-ink sm:text-3xl">
        {title}
      </h2>
      {body === undefined ? null : <p className="text-sm text-pretty text-ink-muted">{body}</p>}
    </div>
  );
}
